// src/services/userManagementService.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://riskassessplus.com/api';

class UserManagementService {
    _handleUnauthorized(response) {
        if (response.status === 401) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('user');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
            throw new Error('Session expired. Please log in again.');
        }
    }

    // Get all users (admin only)
    async getUsers(params = {}, token) {
        const queryParams = new URLSearchParams({
            skip: params.skip || 0,
            limit: params.limit || 100,
            ...(params.search && { search: params.search }),
            ...(params.is_admin !== undefined && { is_admin: params.is_admin })
        });

        try {
            const response = await fetch(`${API_BASE_URL}/admin/users/?${queryParams}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    }

    // Get user by ID (admin only)
    async getUserById(userId, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching user:', error);
            throw error;
        }
    }

    // Update user (admin only)
    async updateUser(userId, userData, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(userData)
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    }

    // Toggle admin status (admin only)
    async toggleAdminStatus(userId, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/toggle-admin`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error toggling admin status:', error);
            throw error;
        }
    }

    // Toggle disabled status (admin only)
    async toggleDisabledStatus(userId, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/disable`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error toggling disabled status:', error);
            throw error;
        }
    }

    // Delete user (admin only)
    async deleteUser(userId, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error deleting user:', error);
            throw error;
        }
    }

    // Get user statistics (admin only)
    async getUserStats(token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/users/stats`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching user stats:', error);
            throw error;
        }
    }
}

export default new UserManagementService();